/**
 * pnl-report.ts — รวม recognition events → P&L 26 บรรทัด + completeness (T069/T071/T072/T073/T077)
 * pure: รับ events ที่ normalize แล้ว (มาจาก DB rows) → PnlInput → computePnl
 *
 * กติกา:
 * - sale รับรู้ที่ paid date; refund ที่ refund date; expense/income ที่ event/policy date
 * - SKU ไม่มีต้นทุน → completeness incomplete + นับ affected revenue (missing-cost engine)
 * - tax-only ไม่รวม; CIT ใช้ยอดจริงที่ส่งเข้ามา
 */
import type Decimal from 'decimal.js';
import { addMoney, moneyToString, toDecimal, type MoneyInput } from './money.js';
import { computePnl, type PnlInput, type PnlLine } from './pnl.js';
import { shiftDateKey, type DateKey } from './datetime.js';

/** หมวด P&L ที่ค่าใช้จ่าย/รายได้ map เข้า (ตรงกับ pnl_category) */
export type PnlCategory =
  | 'marketing_expenses'
  | 'commissions_platform_fees'
  | 'transportation_fulfillment'
  | 'payroll_employee'
  | 'accounting_professional'
  | 'fixed_cost'
  | 'variable_cost'
  | 'other_operating'
  | 'depreciation_amortization'
  | 'interest_finance'
  | 'other_income';

export interface SaleEvent {
  date: DateKey; // paid date (วันที่รับรู้)
  revenue: MoneyInput;
  cost: MoneyInput | null; // null = SKU ไม่มีต้นทุน
}
export interface RefundEvent {
  date: DateKey; // refund date
  amount: MoneyInput; // ค่าบวก (จะหักจาก sales)
}
export interface CostEvent {
  date: DateKey;
  category: PnlCategory;
  amount: MoneyInput;
}

export interface PnlEvents {
  sales: SaleEvent[];
  refunds: RefundEvent[];
  costs: CostEvent[]; // ค่าใช้จ่าย/รายได้อื่น/ค่าธรรมเนียม แยกตามหมวด
  corporateIncomeTax?: MoneyInput; // ยอดจริงที่ยืนยัน
  pbtAdjustments?: MoneyInput;
}

export interface Completeness {
  status: 'complete' | 'incomplete';
  missingCostCount: number;
  affectedRevenue: string;
}

export interface PnlReport {
  lines: PnlLine[];
  completeness: Completeness;
  range: { from: DateKey; to: DateKey };
}

function inRange(d: DateKey, from: DateKey, to: DateKey): boolean {
  return d >= from && d <= to;
}

/**
 * วันที่รับรู้ของรายการรายงวด (payroll/ค่าเสื่อม) = วันแรกของเดือนงวด (T069)
 * งวดรายเดือนรับรู้ทั้งเดือน จึงใช้วันที่ 1 เป็นตัวแทนภายในเดือน (Asia/Bangkok grouping)
 */
export function periodRecognitionDate(year: number, month: number): DateKey {
  const mm = String(month).padStart(2, '0');
  return `${year}-${mm}-01` as DateKey;
}

/** category → field ใน PnlInput */
const CATEGORY_FIELD: Record<PnlCategory, keyof PnlInput> = {
  marketing_expenses: 'marketingExpenses',
  commissions_platform_fees: 'commissionsPlatformFees',
  transportation_fulfillment: 'transportationFulfillment',
  payroll_employee: 'payrollEmployee',
  accounting_professional: 'accountingProfessional',
  fixed_cost: 'fixedCost',
  variable_cost: 'variableCost',
  other_operating: 'otherOperating',
  depreciation_amortization: 'depreciationAmortization',
  interest_finance: 'interestFinance',
  other_income: 'otherIncome',
};

/** รวม events ในช่วง → PnlInput + completeness */
export function buildPnlReport(events: PnlEvents, range: { from: DateKey; to: DateKey }): PnlReport {
  const sums: Record<keyof PnlInput, MoneyInput[]> = {
    sales: [],
    otherIncome: [],
    costOfProducts: [],
    marketingExpenses: [],
    commissionsPlatformFees: [],
    transportationFulfillment: [],
    payrollEmployee: [],
    accountingProfessional: [],
    fixedCost: [],
    variableCost: [],
    otherOperating: [],
    depreciationAmortization: [],
    interestFinance: [],
    corporateIncomeTax: [events.corporateIncomeTax ?? 0],
    pbtAdjustments: [events.pbtAdjustments ?? 0],
  };

  let missingCostCount = 0;
  const affectedRevenue: MoneyInput[] = [];

  for (const s of events.sales) {
    if (!inRange(s.date, range.from, range.to)) continue;
    sums.sales.push(s.revenue);
    if (s.cost === null) {
      missingCostCount++;
      affectedRevenue.push(s.revenue);
    } else {
      sums.costOfProducts.push(s.cost);
    }
  }
  for (const r of events.refunds) {
    if (!inRange(r.date, range.from, range.to)) continue;
    // refund เป็น negative sale (หักจาก sales ในวันที่คืน)
    sums.sales.push(`-${moneyToString(r.amount)}`);
  }
  for (const c of events.costs) {
    if (!inRange(c.date, range.from, range.to)) continue;
    sums[CATEGORY_FIELD[c.category]].push(c.amount);
  }

  const input: PnlInput = {
    sales: addMoney(...sums.sales),
    otherIncome: addMoney(...sums.otherIncome),
    costOfProducts: addMoney(...sums.costOfProducts),
    marketingExpenses: addMoney(...sums.marketingExpenses),
    commissionsPlatformFees: addMoney(...sums.commissionsPlatformFees),
    transportationFulfillment: addMoney(...sums.transportationFulfillment),
    payrollEmployee: addMoney(...sums.payrollEmployee),
    accountingProfessional: addMoney(...sums.accountingProfessional),
    fixedCost: addMoney(...sums.fixedCost),
    variableCost: addMoney(...sums.variableCost),
    otherOperating: addMoney(...sums.otherOperating),
    depreciationAmortization: addMoney(...sums.depreciationAmortization),
    interestFinance: addMoney(...sums.interestFinance),
    pbtAdjustments: addMoney(...sums.pbtAdjustments),
    corporateIncomeTax: addMoney(...sums.corporateIncomeTax),
  };

  return {
    lines: computePnl(input),
    completeness: {
      status: missingCostCount > 0 ? 'incomplete' : 'complete',
      missingCostCount,
      affectedRevenue: moneyToString(addMoney(...(affectedRevenue.length ? affectedRevenue : [0]))),
    },
    range,
  };
}

/** ยอดขายสุทธิรายวัน (ขาย − คืน) ในช่วงที่เลือก — ใช้กับกราฟบนแดชบอร์ด */
export interface DailyPoint {
  date: DateKey;
  sales: string;
}

export function buildDailySeries(
  events: { sales: SaleEvent[]; refunds: RefundEvent[] },
  range: { from: DateKey; to: DateKey },
): DailyPoint[] {
  const byDate = new Map<DateKey, Decimal>();
  const add = (date: DateKey, amount: Decimal): void => {
    if (!inRange(date, range.from, range.to)) return;
    byDate.set(date, (byDate.get(date) ?? toDecimal('0')).plus(amount));
  };
  for (const s of events.sales) add(s.date, toDecimal(s.revenue));
  for (const r of events.refunds) add(r.date, toDecimal(r.amount).negated());

  // เติมวันที่ไม่มีรายการเป็น 0 เพื่อให้กราฟไม่ข้ามวัน
  const out: DailyPoint[] = [];
  let cursor = range.from;
  let guard = 0;
  while (cursor <= range.to && guard < 400) {
    out.push({ date: cursor, sales: moneyToString(byDate.get(cursor) ?? toDecimal('0')) });
    cursor = shiftDateKey(cursor, 1);
    guard += 1;
  }
  return out;
}
